import React from 'react';
import { Grid2, Paper, Typography, Button, Box } from '@mui/material';
import { styled } from '@mui/system';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import GinoLeonPhoto from '../assets/GinoDP.jpeg';

// Styled components
const ProfileCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  borderRadius: 20,
  background: 'linear-gradient(135deg, #ffffff 0%, #f0f4f8 100%)',
  border: '1px solid rgba(173, 223, 173, 0.3)',
  boxShadow: '0 8px 24px rgba(76, 175, 80, 0.1)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 32px rgba(76, 175, 80, 0.2)',
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(3),
  },
}));

const ProfileImage = styled(motion.img)(({ theme }) => ({
  width: '100%',
  maxWidth: 220,
  height: 'auto',
  borderRadius: '5%',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  objectFit: 'cover',
  [theme.breakpoints.down('md')]: {
    maxWidth: 180,
    margin: '0 auto',
    display: 'block',
  },
}));

const AnimatedArrowIcon = styled(ArrowForwardIcon)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  transition: 'transform 0.3s ease-in-out',
  '.MuiButton-root:hover &': {
    animation: 'bounce 0.5s infinite',
  },
  '@keyframes bounce': {
    '0%, 100%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(-5px)' },
  },
}));

// Animation variants
const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut',
    },
  },
};

const imageVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: 'easeOut',
      delay: 0.2,
    },
  },
  hover: {
    scale: 1.05,
    transition: {
      duration: 0.3,
    },
  },
};

const textVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut',
      delay: 0.4,
    },
  },
};

const PersonalProfile = () => {
  return (
    <Box
      id="founder"
      sx={{
        backgroundPosition: 'center',
        padding: { xs: 3, md: 6 },
        minHeight: '50vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Paper
        variants={cardVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        style={{ width: '100%', maxWidth: 1200, marginTop: -100 }}
        sx={{mt:{xs: -10, md: -100}}}
        elevation={0}
      >
          <Grid2 container spacing={4} alignItems="center">
            {/* Photo on the Left */}
            <Grid2 size={{ xs: 12, md: 4 }} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <ProfileImage
                src={GinoLeonPhoto}
                alt="Gino Leon"
                variants={imageVariants}
                initial="hidden"
                animate="visible"
                whileHover="hover"
              />
            </Grid2>

            {/* Information on the Right */}
            <Grid2 size={{ xs: 12, md: 8 }}>
              <motion.div variants={textVariants} initial="hidden" animate="visible">
                <Typography
                  variant="h4"
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    color: '#2e7d32',
                    mb: 1,
                    fontSize: { xs: 28, md: 36 },
                  }}
                >
                  Meet Gino Leon
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: '#388e3c',
                    mb: 2,
                    fontSize: { xs: 18, md: 20 },
                    fontWeight: 500,
                  }}
                >
                  Founder of Maths Gem Seychelles
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#1b5e20',
                    lineHeight: 1.8,
                    fontSize: { xs: 16, md: 18 },
                    mb: 3,
                  }}
                >
                  Gino Leon, an engineer by profession who achieved an A in A-Level Mathematics, is passionate about transforming mathematics education in Seychelles. By offering engaging lessons in Creole, he empowers Primary and Secondary students to learn with confidence and shine in their studies.
                </Typography>
              </motion.div>
            </Grid2>
          </Grid2>
      </Paper>
    </Box>
  );
};

export default PersonalProfile;
